/** Mutation type registry for Rogue's Cog */

export enum MutationType {
  RowInject = 'row_inject',
  ColorShift = 'color_shift',
  GravityNudge = 'gravity_nudge',
  ObstacleSpawn = 'obstacle_spawn',
  RainbowRow = 'rainbow_row',
  ScoreFrenzy = 'score_frenzy',
  BubbleBomb = 'bubble_bomb',
  SlowClockwork = 'slow_clockwork',
  CascadeCoil = 'cascade_coil',
  WildcardGear = 'wildcard_gear',
  NarrowSpectrum = 'narrow_spectrum',
  LubricatedBearings = 'lubricated_bearings',
  GovernorLock = 'governor_lock',
}

export interface MutationDef {
  readonly name: string;
  readonly description: string;
  readonly flavorText: string;
  readonly sentiment: 'harmful' | 'beneficial' | 'mixed';
  readonly defaultParams: Record<string, number>;
  /**
   * Flavor line shown on the Mk. V (max-level) draft card in place of
   * `flavorText`. Beneficial blueprints only — see mutations/levels.ts.
   */
  readonly maxFlavorText?: string;
}

export const REGISTRY: Record<MutationType, MutationDef> = {
  [MutationType.RowInject]: {
    name: 'Row Inject',
    description: 'A new row of gears drops in at the top, pushing the board down.',
    flavorText: 'The ceiling presses ever closer now.',
    sentiment: 'harmful',
    defaultParams: { rows: 1 },
  },
  [MutationType.ColorShift]: {
    name: 'Color Fracture',
    description: 'Many gears of the most common color turn other colors.',
    flavorText: 'The kilns disagree. Your best-laid rows lose their nerve.',
    sentiment: 'harmful',
    defaultParams: {},
  },
  [MutationType.GravityNudge]: {
    name: 'Gravity Nudge',
    description: 'Part of the board slides one column sideways.',
    flavorText: 'The tower lurches under its weight.',
    sentiment: 'harmful',
    defaultParams: { direction: 1 },
  },
  [MutationType.ObstacleSpawn]: {
    name: 'Obstacle Spawn',
    description: 'Unmatchable stone blocks appear next to your gears.',
    flavorText: 'Stone blocks seal off precious space.',
    sentiment: 'harmful',
    defaultParams: { count: 2 },
  },
  [MutationType.RainbowRow]: {
    name: 'Prismatic Spark',
    description: 'A few random gears dissolve in prismatic light.',
    flavorText: 'Gears shatter into rainbow shards. What hung above comes crashing down.',
    sentiment: 'beneficial',
    defaultParams: { count: 3 },
    maxFlavorText: 'Gears burst into light across the tower. Whole spans forget what held them up.',
  },
  [MutationType.ScoreFrenzy]: {
    name: 'Score Frenzy',
    description: 'Base points are doubled for the next 5 turns.',
    flavorText: 'The furnace roars — every strike lands twice as hot.',
    sentiment: 'beneficial',
    defaultParams: { turns: 5 },
    maxFlavorText: 'The furnace door is welded open. White heat that will not fade.',
  },
  [MutationType.BubbleBomb]: {
    name: 'Powder Keg',
    description: 'Your next shot becomes a powerful bomb bubble.',
    flavorText: 'A lit fuse sits in the cannon. Aim for crowds.',
    sentiment: 'beneficial',
    defaultParams: {},
    maxFlavorText: 'The keg is packed to the hoops. Whatever you aim at stops being a tower.',
  },
  [MutationType.SlowClockwork]: {
    name: 'Slow the Clockwork',
    description: 'The descending ceiling is paused for the next 5 turns.',
    flavorText: 'The great gear groans to a halt.',
    sentiment: 'beneficial',
    defaultParams: { turns: 5 },
    maxFlavorText: 'The great gear is chained and pinned. Nothing above you moves until you say so.',
  },
  [MutationType.CascadeCoil]: {
    name: 'Cascade Coil',
    description: 'Your next shot is an electric orb: on contact, gears of every colour it touches shake loose from elsewhere on the board. The orb itself dissipates — nothing lands.',
    flavorText: 'Your shot hums a note. Kindred cogs answer, breaking loose across the tower.',
    sentiment: 'beneficial',
    defaultParams: { removals: 3 },
    maxFlavorText: 'One touch, and every kindred cog in the tower answers by falling.',
  },
  [MutationType.WildcardGear]: {
    name: 'Wildcard Gear',
    description: 'Your next shot is a wild gear that joins any cluster it touches.',
    flavorText: 'Brass polished to a mirror — it wears any color you need.',
    sentiment: 'beneficial',
    defaultParams: { shots: 1 },
    maxFlavorText: 'Mirrors stacked deep. Whatever the tower asks for, the brass already is.',
  },
  [MutationType.NarrowSpectrum]: {
    name: 'Narrow Spectrum',
    description: 'For the next 5 shots, only 3 colors are drawn from.',
    flavorText: 'The kiln cools. Only a few hues survive the drawing.',
    sentiment: 'beneficial',
    defaultParams: { shots: 5, colors: 3 },
    maxFlavorText: 'The kiln goes cold and stays cold. A narrow palette, drawn and drawn again, no surprises.',
  },
  [MutationType.LubricatedBearings]: {
    name: 'Bumper Brass',
    description: 'Bank shots pay 2.5x per wall bounce (up from 1.5x) for 5 turns.',
    flavorText: 'Oiled bumpers hum. Every kiss of the wall rings a richer coin.',
    sentiment: 'beneficial',
    defaultParams: { turns: 5 },
    maxFlavorText: 'The bumpers sing on and on. Every wall in the tower is a toll paid to you.',
  },
  [MutationType.GovernorLock]: {
    name: 'Governor Lock',
    description: 'The score multiplier does not decay on misses for 5 turns.',
    flavorText: 'The governor binds. Momentum cannot slip.',
    sentiment: 'beneficial',
    defaultParams: { turns: 5 },
    maxFlavorText: 'The governor is seized solid. Whatever you build, nothing slips.',
  },
};
